import { postCall } from "./api";

/**
 * Putting a file where a link can point at it.
 *
 * The file goes straight from the browser to S3 using a URL the server signs,
 * so it never passes through our API. That is what makes uploading a payslip
 * or an Aadhaar scan cheap — no request body through the app server, no
 * temporary copy on it — and it is the flow the signing endpoint was always
 * built for; nothing had ever called it.
 *
 * The stored address is the object URL, which is the signed URL with its
 * signature stripped: the signature is a 60-second grant to *write*, and
 * keeping it on a document record would be storing a credential.
 */

// Generous enough for a phone photo of a document, small enough that a
// mis-selected video is refused here rather than after a two-minute upload.
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const ALLOWED = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".heic"];

export const describeUploadLimits = () =>
  `PDF or image, up to ${Math.round(MAX_UPLOAD_BYTES / (1024 * 1024))}MB`;

// Named by the person and the moment rather than left as the phone's
// "IMG_4821.jpg", so a folder of these is navigable later.
const safeName = (name = "file") =>
  name
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .slice(-80);

export const uploadFile = async (file) => {
  if (!file) return { success: false, message: "No file chosen" };
  if (file.size > MAX_UPLOAD_BYTES) {
    return { success: false, message: `That file is too large — ${describeUploadLimits()}` };
  }
  const dot = file.name.lastIndexOf(".");
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : "";
  if (!ALLOWED.includes(ext)) {
    return { success: false, message: `That file type is not accepted — ${describeUploadLimits()}` };
  }

  const signed = await postCall({ path: `/s3/generate-url/${safeName(file.name)}` });
  const uploadUrl = signed?.data;
  if (signed?.success === false || typeof uploadUrl !== "string") {
    return { success: false, message: signed?.message || "Couldn't prepare the upload" };
  }

  try {
    // Straight to S3. Not getCall/postCall: those talk to our API, add an
    // Authorization header S3 would reject, and unwrap a JSON body that a
    // successful PUT here does not return.
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!res.ok) {
      return { success: false, message: `Upload failed (${res.status})` };
    }
    return { success: true, url: uploadUrl.split("?")[0] };
  } catch (err) {
    return { success: false, message: err?.message || "Upload failed" };
  }
};
